import React, { useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { Container, Grid } from '@mui/material';
import {
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  MultiSelectDropdown,
  Snackbar,
  Alert,
} from '../../components/basic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import InputAdornment from '@mui/material/InputAdornment';
import { useLazyParseJdQuery } from '../../redux/services/ml';
import { useNavigate } from 'react-router-dom';
import {
  useFindExperiencesQuery,
  useFindSkillsQuery,
  useLazyFindPlacesQuery,
} from '../../redux/services/sundries';

const SEARCH_TYPES = {
  JD: 'SEARCH_BY_JD',
  KEYWORDS: 'SEARCH_BY_KEYWORDS',
};

const HomePage = () => {
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(SEARCH_TYPES.JD);
  const [keywordsText, setKeywordsText] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedExperiences, setSelectedExperiences] = useState([]);
  const [locationSearchText, setLocationSearchText] = useState('');
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');

  const [
    callJdParser,
    {
      data: parsedJd,
      isSuccess: isParsingSuccess,
      isFetching: isParsingFetching,
    },
  ] = useLazyParseJdQuery();

  const [
    searchPlaces,
    { data: locationOptions = [], isFetching: isPlacesFetching },
  ] = useLazyFindPlacesQuery();
  const { data: experienceOptions = [], isFetching: isExperiencesFetching } =
    useFindExperiencesQuery();
  const { data: skillOptions = [], isFetching: isSkillsFetching } =
    useFindSkillsQuery();

  const navigate = useNavigate();

  const handleTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleSearchTypeChange = (event, value) => {
    setSearchType(value);
  };

  const handleSearchClick = () => {
    callJdParser(searchText);
  };

  const getMultiSelectLabel = (label, items) =>
    items.length ? `${items.length} ${label}` : label;

  const handleKeywordsTextChange = (event) => {
    setKeywordsText(event.target.value);
  };

  const handleSkillChange = (values) => {
    setSelectedSkills(values);
  };

  const handleLocationChange = (values) => {
    setSelectedLocations(values);
  };

  const handleLocationSearchChange = (e, text) => {
    setLocationSearchText(text);
    searchPlaces(text);
  };

  const handleExperienceChange = (values) => {
    setSelectedExperiences(values);
  };

  const handleSearchByKeywordsClick = () => {
    if (!selectedSkills.length) {
      setSnackMessage('Select a few skills');
      setIsSnackBarOpen(true);
      return;
    }
    if (!selectedLocations.length) {
      setSnackMessage('Select a location');
      setIsSnackBarOpen(true);
      return;
    }
    if (!selectedExperiences.length) {
      setSnackMessage('Select a few experiences');
      setIsSnackBarOpen(true);
      return;
    }

    const filterState = {
      searchText: keywordsText,
      jobTitle: null,
      skills: {
        values: selectedSkills,
        weight: 0,
      },
      locations: {
        values: selectedLocations,
        weight: 0,
      },
      experiences: {
        values: selectedExperiences,
        weight: 7,
      },
    };
    navigate('/search', { state: { ...filterState } });
  };

  React.useEffect(() => {
    if (!isParsingFetching && isParsingSuccess) {
      console.log('parsed :', parsedJd);
      if (
        !(parsedJd.locations.values.length || parsedJd.skills.values.length)
      ) {
        setSnackMessage('Insufficient text. Enter few more words!');
        setIsSnackBarOpen(true);
        return;
      }
      navigate('/search', { state: { ...parsedJd, searchText } });
    }
  }, [isParsingFetching, isParsingSuccess]);

  return (
    <>
      <Container maxWidth="md" sx={{ my: 2 }}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h1">Rikroot.ai</Typography>
            <Typography variant="h6">This is a tag line</Typography>
          </Grid>
          <Grid item xs={6} container spacing={1}>
            <Grid item container spacing={1} alignItems="center">
              <Grid item xs="auto">
                <Typography variant="body1">Search by</Typography>
              </Grid>
              <Grid item xs>
                <ToggleButtonGroup
                  value={searchType}
                  size="small"
                  exclusive
                  fullWidth
                  color="primary"
                  onChange={handleSearchTypeChange}
                  aria-label="view style"
                >
                  <ToggleButton
                    value={SEARCH_TYPES.JD}
                    aria-label={SEARCH_TYPES.JD}
                  >
                    Raw JD
                  </ToggleButton>
                  <ToggleButton
                    value={SEARCH_TYPES.KEYWORDS}
                    aria-label={SEARCH_TYPES.KEYWORDS}
                  >
                    Keywords
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            </Grid>
            {searchType === SEARCH_TYPES.JD && (
              <Grid
                item
                xs={12}
                container
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <TextField
                    placeholder="Paste your Job Description here"
                    value={searchText}
                    multiline
                    minRows={10}
                    maxRows={20}
                    onChange={handleTextChange}
                  />
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSearchClick}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            )}
            {searchType === SEARCH_TYPES.KEYWORDS && (
              <Grid item xs container spacing={1} justifyContent="flex-end">
                <Grid item xs={12}>
                  <TextField
                    value={keywordsText}
                    placeholder="Keywords"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <FontAwesomeIcon icon={faSearch} />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleKeywordsTextChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectDropdown
                    id="dd-skills"
                    multiple
                    value={selectedSkills}
                    items={skillOptions}
                    getButtonLabel={(selected) =>
                      getMultiSelectLabel('Skill', selected)
                    }
                    onChange={handleSkillChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectDropdown
                    id="dd-locations"
                    multiple
                    value={selectedLocations}
                    noOptionsText={
                      locationSearchText.length
                        ? isPlacesFetching
                          ? 'Searching..'
                          : 'No Results Found'
                        : 'Enter a few characters to get results'
                    }
                    items={locationOptions.concat(selectedLocations)}
                    getButtonLabel={(selected) =>
                      getMultiSelectLabel('Location', selected)
                    }
                    onChange={handleLocationChange}
                    onInputChange={handleLocationSearchChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultiSelectDropdown
                    id="dd-experience"
                    multiple
                    value={selectedExperiences}
                    items={experienceOptions}
                    getButtonLabel={(selected) =>
                      getMultiSelectLabel('Experience', selected)
                    }
                    onChange={handleExperienceChange}
                  />
                </Grid>
                <Grid item xs="auto">
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faSearch} />}
                    onClick={handleSearchByKeywordsClick}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={isSnackBarOpen}
          onClose={() => setIsSnackBarOpen(false)}
        >
          <Alert severity="error">{snackMessage}</Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default HomePage;
